/**
 * commentPost — the two "Add your take…" composers post a real comment.
 *
 * WHAT THEY DID. Both composers run the same fabrication. The inline one on a
 * card (`postTake`, app.js:4109) and the comments modal's (app.js:4308) each
 * push `{by: store.me, text, when: "now", likes: 0}` straight onto the LISTINGS
 * row's `comments` array, bump `commentCount`, repaint, and — for the inline
 * one — toast "Take posted". No POST. The comment appears under a real listing
 * with the visitor's own handle on it, and is gone on the next reload.
 *
 * 🔴 THAT ROW BELONGS TO A REAL SELLER. installFloorListings replaces LISTINGS
 * wholesale from GET /api/listings and installLiveListingComments fills
 * `comments` from GET /api/comments/:listingId, so the array being written to
 * is real data about a real person's item. writeGuard deadened both controls
 * for that reason, and the owner reported the result as a broken button
 * (2026-08-28) — which is the honest reading of a control that does nothing.
 *
 * Now they post. POST /api/comments/:listingId (comments.ts:241) is
 * requireAuth + writeLimiter, there is no moderation queue, and it has real
 * refusals worth showing rather than swallowing: 409 when the listing's status
 * is not engageable, 403 when either party has blocked the other, 400 on an
 * empty body, 404 on a listing that no longer exists.
 *
 * 🔑 A SUCCESSFUL POST RE-FETCHES THE THREAD INSTEAD OF APPENDING LOCALLY.
 * Hand-building a comment object to push onto the array is exactly what the
 * fabricator did, and it is how the rendered thread starts drifting from the
 * stored one: `when: "now"` instead of a real timestamp, the visitor's handle
 * instead of the author record the server actually wrote, no `id`, and nothing
 * to catch a body the server normalized or truncated. Re-fetching runs the
 * SAME path installLiveListingComments already uses — mapCommentRow,
 * mapCommentAuthors, __gbzSetListingComments — so what appears on screen is
 * what the server has, avatar and handle included. The cost is one extra round
 * trip, which is the right price for not inventing a row.
 *
 * 🪤 THE MODAL COMPOSER HAS NO LISTING ID IN ITS MARKUP. `#cm-send` and
 * `#cm-in` are bare ids; the listing lives in `commentsFor` (app.js:208), a
 * top-level `let` and therefore a lexical binding invisible to this module.
 * __gbzCommentsFor is the bridge accessor that makes the modal wirable at all.
 *
 * 🪤 TWO EVENTS, NOT ONE, and the asymmetry is Blake's not ours. `#cm-in`'s
 * Enter synthesises `$("#cm-send").click()` (app.js:4318), and a synthetic
 * click dispatches through the full capture path, so the click handler below
 * sees it. `[data-takefor]`'s Enter calls `postTake` DIRECTLY (app.js:4125) —
 * no click is ever dispatched, so no click-phase handler can see it. That one
 * needs its own keydown listener. Same split writeGuard already documents in
 * GUARDED_ENTER_INPUTS, verified the same way.
 *
 * BOTH CONTROLS STAY IN GUARDED_WRITES. The guard is installed first, on the
 * same document, in capture, and uses stopPropagation rather than
 * stopImmediatePropagation — so both listeners run: the guard kills the
 * fabricator, this module does the real POST. Removing the guard entries would
 * not unblock anything, it would resurrect the invented comment alongside the
 * genuine one. The ordering is also the fail-safe: if this module ever fails to
 * install, the composers go back to being merely dead rather than lying.
 */

import { mapCommentRow, mapCommentAuthors, type ApiCommentRow, type DemoComment, type DemoUser } from "./floorTransport";
import { signedInOrWall } from "./guestWall";

/** The inline card composer's button, listing id in the attribute (app.js:181, :964). */
export const TAKE_SEND_SELECTOR = "[data-takesend]";
/** Its input. Enter here bypasses the click path entirely — see the header. */
export const TAKE_INPUT_SELECTOR = "[data-takefor]";
/** The comments modal's button (app.js:239). Subject comes from the bridge. */
export const MODAL_SEND_SELECTOR = "#cm-send";
/** The modal's input (app.js:238). Its Enter synthesises a click, so it needs no keydown. */
export const MODAL_INPUT_SELECTOR = "#cm-in";

/** Reused from the other interceptors, which face the same 401/403. */
export const SIGNIN_MESSAGE = "Sign in to post a comment";
/**
 * Blake's own word for it (app.js:4117), and it is TRUE now.
 *
 * 🔴 THE INLINE CARD COMPOSER GIVES NO OTHER FEEDBACK. His card preview
 * renders `l.comments[0]` (app.js:176) -- the FIRST row of the array, not the
 * newest -- so a comment you just posted never appears there. The only visible
 * change is the "View all N" count ticking by one.
 *
 * Found by an adversarial review probe that drove this module against the REAL
 * app.js in jsdom rather than a hand-written fixture: post, and the card's
 * rendered HTML is unchanged and nothing is said. The fabricator this replaced
 * at least toasted. Dropping the toast made the perceived behaviour WORSE than
 * the lie it removed, on the exact control the owner had already reported as
 * broken.
 *
 * 📌 INLINE ONLY. The modal maps every comment (app.js:227), so yours appears
 * in the list the moment the repaint lands -- which is why Blake put no toast
 * there either. A toast on top of visible feedback is noise.
 */
export const TAKE_POSTED_MESSAGE = "Take posted";

/** Only for a failure the server gave no sentence for. */
export const GENERIC_FAILURE_MESSAGE = "Comment failed — please try again";

interface CommentWindow extends Window {
  __gbzCommentsFor?: () => string | null;
  __gbzSetListingComments?: (id: string, comments: DemoComment[]) => boolean;
  __gbzMergeUsers?: (users: Record<string, DemoUser>) => number;
  rerenderKeepScroll?: () => void;
  render?: () => void;
  toast?: (message: string) => void;
  /** app.js's send-disc toggle (the gold "armed" state). */
  armTake?: (input: HTMLInputElement) => void;
}

/** Which composer a click or keypress belongs to, and what it is about. */
interface Composer {
  listingId: string;
  input: HTMLInputElement;
  /** Drives the success toast: the card preview cannot show the new comment,
   *  the modal list can. See TAKE_POSTED_MESSAGE. */
  kind: "inline" | "modal";
}

export function installCommentPost(
  win: Window,
  authFetch: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>,
  {
    notify = (message: string) => {
      // #toast only exists after app.js's first render; a click that somehow
      // arrives before it degrades to silence rather than throwing inside a
      // capture listener. Same lifecycle guard as checkoutCtaGuard.
      if (!win.document.getElementById("toast")) return;
      (win as CommentWindow).toast?.(message);
    },
  }: { notify?: (m: string) => void } = {},
): () => void {
  /* In flight. Blake's handlers carry no re-entrancy guard because his were
     synchronous; the moment a real await sits in here, a double-tap is two
     comments. Keyed by listing id rather than a single flag, so posting on one
     card never blocks another. */
  const busy = new Set<string>();

  /** The inline composer: id on the button, input is its sibling. */
  function inlineComposer(el: Element): Composer | null {
    const listingId = (el as HTMLElement).dataset?.takesend;
    if (!listingId) return null;
    const input = el.parentElement?.querySelector<HTMLInputElement>(".take-in");
    return input ? { listingId, input, kind: "inline" } : null;
  }

  /** The modal composer: id from the bridge, input by its own id. */
  function modalComposer(): Composer | null {
    const listingId = (win as CommentWindow).__gbzCommentsFor?.();
    if (!listingId) return null;
    const input = win.document.querySelector<HTMLInputElement>(MODAL_INPUT_SELECTOR);
    return input ? { listingId, input, kind: "modal" } : null;
  }

  /**
   * Re-read the thread from the server and hand it to the bundle.
   *
   * Best-effort by design: the comment is already stored by the time this runs,
   * so a failure here costs a repaint, not the comment. Saying "Comment failed"
   * because the re-read failed would be its own false claim.
   */
  async function refresh(listingId: string): Promise<void> {
    const res = await authFetch(`/api/comments/${encodeURIComponent(listingId)}`);
    if (!res.ok) return;
    const rows = (await res.json()) as ApiCommentRow[];
    const target = win as CommentWindow;
    /* AUTHORS FIRST, COMMENTS SECOND — the same ordering installLiveListingComments
       documents. The repaint below is what puts handles and avatars on screen and
       both read USERS, so merging after it paints a frame of "?" avatars. */
    target.__gbzMergeUsers?.(mapCommentAuthors(rows));
    target.__gbzSetListingComments?.(listingId, rows.map((r) => mapCommentRow(r)));
    /* rerenderKeepScroll, NOT render: you post a take on a card halfway down the
       feed, and render() would send you back to the top of it. It is a top-level
       function declaration (app.js:3888) and therefore a real window property,
       unlike the `let`s this lane needs a bridge for. */
    if (typeof target.rerenderKeepScroll === "function") target.rerenderKeepScroll();
    else target.render?.();
  }

  async function post(composer: Composer): Promise<void> {
    const body = composer.input.value.trim();
    // Blake returns silently on empty and so do we: an error toast for pressing
    // send on an empty box is noise, not information.
    if (!body) return;

    const res = await authFetch(`/api/comments/${encodeURIComponent(composer.listingId)}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ body }),
    });

    if (res.status === 401) {
      notify(SIGNIN_MESSAGE);
      return;
    }
    if (!res.ok) {
      /* The server's own words when it gave any, ours only when it did not.
         403 here is NOT a sign-in problem — it is "You cannot comment on this
         listing" (a block), and 409 is "Comments are closed on this listing"
         (the listing left an engageable status). Both are things the visitor
         needs to read, so neither is collapsed into the sign-in sentence. */
      const payload = (await res.json().catch(() => ({}))) as { error?: string };
      notify(payload.error ?? GENERIC_FAILURE_MESSAGE);
      return;
    }

    // Stored. Clear the box first so the repaint cannot race the clear.
    composer.input.value = "";
    /* A programmatic clear fires no input event, so the send disc's gold "armed"
       state outlived the text it stood for whenever the re-read below failed and
       nothing repainted. armTake is app.js's own toggle. */
    (win as CommentWindow).armTake?.(composer.input);
    await refresh(composer.listingId);
    /* AFTER the repaint, not before: the toast is the only signal the inline
       composer gives, and saying it while the thread is still the old one
       would put the claim ahead of the evidence for it. */
    if (composer.kind === "inline") notify(TAKE_POSTED_MESSAGE);
  }

  function submit(composer: Composer | null): void {
    if (!composer) return;
    if (busy.has(composer.listingId)) return;
    busy.add(composer.listingId);
    void post(composer)
      .catch(() => notify(GENERIC_FAILURE_MESSAGE))
      .finally(() => busy.delete(composer.listingId));
  }

  const onClick = (event: Event): void => {
    const target = event.target as Element | null;
    if (!target?.closest) return;

    const take = target.closest(TAKE_SEND_SELECTOR);
    if (take) {
      // A guest gets the sign-in wall and no request — see guestWall.ts.
      if (!signedInOrWall(win)) return;
      submit(inlineComposer(take));
      return;
    }
    if (target.closest(MODAL_SEND_SELECTOR)) {
      if (!signedInOrWall(win)) return;
      submit(modalComposer());
    }
  };

  const onKeydown = (event: Event): void => {
    const ke = event as KeyboardEvent;
    if (ke.key !== "Enter") return;
    const el = event.target as Element | null;
    // ONLY the inline input. #cm-in's Enter synthesises a click, which onClick
    // already sees; handling it here too would post the same comment twice.
    if (!el?.matches?.(TAKE_INPUT_SELECTOR)) return;
    const listingId = (el as HTMLElement).dataset?.takefor;
    if (!listingId) return;
    if (!signedInOrWall(win)) return;
    submit({ listingId, input: el as HTMLInputElement, kind: "inline" });
  };

  // Capture on the document, same posture as writeGuard and the other
  // interceptors: Blake binds on the element in the target phase, so this runs
  // first. Deliberately NOT stopping propagation — letting the guard run is
  // what keeps the fabricator dead. See the header.
  win.document.addEventListener("click", onClick, true);
  win.document.addEventListener("keydown", onKeydown, true);
  return () => {
    win.document.removeEventListener("click", onClick, true);
    win.document.removeEventListener("keydown", onKeydown, true);
  };
}
